import { AfterContentInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { finalize, switchMap, tap } from 'rxjs/operators';

import { ErrorHandlerService, UserService, AuthenticationService, NotyService } from '@app/services';
import { LocaleInput, Signer, User } from '@app/models';
import { ConfirmOtpModalService } from '@app/shared';
import { AuthEmailConfirmationModalService } from '../auth-email-confirmation-modal/auth-email-confirmation-modal.service';

export type ModalResult = User;
export interface ModalPublicProperties {
  signer?: Signer;
  email?: string;
  documentLocale?: LocaleInput;
}

@Component({
  selector: 'app-auth-login-modal',
  templateUrl: './auth-login-modal.component.html',
  styleUrls: ['./auth-login-modal.component.scss'],
  standalone: false
})
export class AuthLoginModalComponent implements ModalPublicProperties, AfterContentInit {
  @Input() signer: Signer;
  @Input() email: string;
  @Input() documentLocale?: LocaleInput;
  form!: UntypedFormGroup;
  isLoading = false;
  fixedEmail: string;
  @ViewChild('emailInput', { static: false }) private emailInput: ElementRef<HTMLInputElement>;
  @ViewChild('passwordInput', { static: false }) private passwordInput: ElementRef<HTMLInputElement>;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private authenticationService: AuthenticationService,
    private errorHandlerService: ErrorHandlerService,
    private userService: UserService,
    private notyService: NotyService,
    private authEmailConfirmationModalService: AuthEmailConfirmationModalService,
    private confirmOtpModalService: ConfirmOtpModalService
  ) {
    this.form = this.formBuilder.group({ email: '', password: '' });
  }

  ngAfterContentInit() {
    if (this.signer || this.email) {
      this.fixedEmail = this.signer?.email || this.email;
      this.form.get('email').setValue(this.fixedEmail || '');
      setTimeout(() => this.passwordInput.nativeElement.focus());
    } else {
      setTimeout(() => this.emailInput.nativeElement.focus());
    }
  }

  login() {
    this.isLoading = true;
    this.form.markAllAsTouched();

    this.authenticationService.loginEmail(this.form.getRawValue()).subscribe({
      next: credentials => {
        if (credentials.mfa.length > 0) {
          this.confirmOtpModalService
            .open({ logoutOnDismiss: true })
            .pipe(
              switchMap(() => this.loadUserFinish()),
              finalize(() => (this.isLoading = false))
            )
            .subscribe();
        } else {
          this.loadUserFinish().subscribe();
        }
      },
      error: error => {
        this.isLoading = false;
        if (error && (error.status || (error.networkError && error.networkError.status)) === 424) {
          this.authEmailConfirmationModalService.open({ email: this.form.get('email').value, documentLocale: this.documentLocale }).subscribe({ next: () => this.login() });
        } else {
          this.errorHandlerService.handleValidation(this.form, error);
        }
      }
    });
  }

  sendResetPasswordConfirmation() {
    this.isLoading = true;
    this.authenticationService
      .sendResetPasswordConfirmation(this.form.get('email').value)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => this.notyService.success(this.translateService.instant('notyService.resetPasswordEmailSent')),
        error: error => this.errorHandlerService.handle(error)
      });
  }

  private loadUserFinish() {
    return this.userService.getCurrentUser({ fetchPolicy: 'network-only' }).pipe(
      tap({
        next: data => setTimeout(() => this.modal.close(data as ModalResult)),
        error: error => this.errorHandlerService.handle(error)
      }),
      finalize(() => (this.isLoading = false))
    );
  }
}
